import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Megaphone, Plus, Edit3, Trash2, Pin, Loader2, Send
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const AnnouncementsPage = () => {
  const { user, api, isAdmin } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', pinned: false });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/announcements');
        setAnnouncements(res.data);
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/announcements/${editingId}`, form);
        const res = await api.get('/announcements');
        setAnnouncements(res.data);
      } else {
        const res = await api.post('/announcements', form);
        setAnnouncements(prev => [res.data, ...prev]);
      }
      setForm({ title: '', content: '', pinned: false });
      setShowForm(false);
      setEditingId(null);
    } catch (e) {
      // Non-critical error suppressed
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (a) => {
    setForm({ title: a.title, content: a.content, pinned: a.pinned });
    setEditingId(a.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/announcements/${id}`);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      // Non-critical error suppressed
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const pinned = announcements.filter(a => a.pinned);
  const regular = announcements.filter(a => !a.pinned);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="announcements-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl font-semibold text-text-primary flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-brand-accent" />
            Community Board
          </h1>
          <p className="text-text-secondary mt-2">Announcements and updates from the admins</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ title: '', content: '', pinned: false }); }}
            className="btn-accent gap-2"
            data-testid="new-announcement-btn"
          >
            <Plus className="w-4 h-4" />
            New Post
          </Button>
        )}
      </div>

      {/* Admin Form */}
      {showForm && isAdmin && (
        <Card className="border-brand-accent/30 bg-brand-accent/5 mb-8" data-testid="announcement-form">
          <CardContent className="p-6">
            <h2 className="font-serif text-lg font-semibold text-text-primary mb-4">
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  placeholder="Title (e.g., Welcome New Members!)"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="text-lg font-medium"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Textarea
                  placeholder="Write your announcement here... You can welcome new members, share updates, announce events, etc."
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={6}
                  className="resize-none"
                  data-testid="announcement-content-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={form.pinned}
                    onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                    className="w-4 h-4 rounded border-brand-primary/30 text-brand-accent focus:ring-brand-accent"
                  />
                  <Pin className="w-3.5 h-3.5" />
                  Pin to top
                </label>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="btn-accent gap-2" data-testid="submit-announcement-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {editingId ? 'Update' : 'Post'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-20">
          <Megaphone className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary">No announcements yet.</p>
          {isAdmin && <p className="text-sm text-text-muted mt-2">Click "New Post" to share something with the community.</p>}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Pinned */}
          {pinned.map((a) => (
            <Card key={a.id} className="border-brand-accent/30 bg-brand-accent/5" data-testid={`announcement-${a.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarImage src={a.author_avatar} />
                    <AvatarFallback className="bg-brand-accent text-white">{a.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Pin className="w-3.5 h-3.5 text-brand-accent" />
                      <span className="text-xs font-medium text-brand-accent uppercase tracking-wider">Pinned</span>
                    </div>
                    <h2 className="font-serif text-xl font-semibold text-text-primary mb-2">{a.title}</h2>
                    <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">{a.content}</p>
                    <div className="flex items-center gap-3 mt-4 text-xs text-text-muted">
                      <span>{a.author_name}</span>
                      <span>{timeAgo(a.created_at)}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} className="text-text-muted hover:text-brand-accent h-8 w-8 p-0">
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-text-muted hover:text-red-500 h-8 w-8 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Regular */}
          {regular.map((a) => (
            <Card key={a.id} className="border-brand-primary/10" data-testid={`announcement-${a.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarImage src={a.author_avatar} />
                    <AvatarFallback className="bg-brand-primary text-white">{a.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-serif text-xl font-semibold text-text-primary mb-2">{a.title}</h2>
                    <p className="text-text-secondary leading-relaxed whitespace-pre-wrap">{a.content}</p>
                    <div className="flex items-center gap-3 mt-4 text-xs text-text-muted">
                      <span>{a.author_name}</span>
                      <span>{timeAgo(a.created_at)}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(a)} className="text-text-muted hover:text-brand-accent h-8 w-8 p-0">
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-text-muted hover:text-red-500 h-8 w-8 p-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsPage;
